import { FloatingPortal } from '@floating-ui/react';
import { useTour } from '@reactour/tour';
import React, { useCallback, useMemo, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { GET_STARTED_TOURS, TOURS } from '.';
import { IconButton } from '../../../../../components/button';
import Icon from '../../../../../components/icon/Icon';
import { useRedirect } from '../../../../../hooks/useRedirect';
import { setTourSelected } from '../../../../../redux/actions/tour';
import routes from '../../../../../routes/constants';
import text from '../../../../../text';
import { isRouteIncluded } from '../../../../../utilities/routes';
import { useGetLatestProjectQuery } from '../../../../../redux/rtk';
import { isDemoProject } from '../../../../../utilities/project';
import cn from 'classnames';

interface GetStartedButtonProps {
  classNames?: string;
}

const GetStartedButton: React.FC<GetStartedButtonProps> = ({ classNames }) => {
  const dispatch = useDispatch();
  const { setSteps, setCurrentStep } = useTour();
  const redirect = useRedirect();
  const location = useLocation();
  const [showMenu, setShowMenu] = useState(false);
  const { data: latestProject } = useGetLatestProjectQuery({
    isAdmin: false,
    includeDemo: true,
    includeJobs: true,
    includePointCloud: false,
  });

  const shouldShowGetStartedButton = useMemo(
    () =>
      isRouteIncluded([
        routes.dashboard,
        routes.fileManager.root,
        routes.user.profile,
        routes.mp.job(undefined),
      ])(location.pathname),
    [location.pathname]
  );

  const toggleDropdown = (isOpen: boolean) => {
    setShowMenu(isOpen);
  };

  const handleOpenFileManagerTour = useCallback(async () => {
    if (!latestProject?.id) return;
    if (!setSteps || !setCurrentStep) {
      console.warn('Tour functions not available');
      return;
    }
    const tour = GET_STARTED_TOURS[TOURS.TOUR_FILE_MANAGER];
    if (!tour) return;
    dispatch(setTourSelected(TOURS.TOUR_FILE_MANAGER));
    setSteps(tour);
    setCurrentStep(0);

    const isDemo = isDemoProject(latestProject);
    const fileManagerRoute = routes.fileManager.viewProject(latestProject.id);
    if (!isRouteIncluded([fileManagerRoute])(location.pathname)) {
      redirect.push(fileManagerRoute, {
        ...(isDemo && {
          preserveQueryParams: true,
          queryParams: { isDemo: true },
        }),
      });
    }
  }, [
    dispatch,
    latestProject,
    location.pathname,
    redirect,
    setCurrentStep,
    setSteps,
  ]);

  return (
    shouldShowGetStartedButton && (
      <div
        id='tour-menu'
        className={cn({
          [classNames as string]: classNames,
        })}
      >
        <Dropdown
          bsPrefix='dropdown-tour-container'
          onToggle={toggleDropdown}
          show={showMenu}
        >
          <Dropdown.Toggle id='dropdown-tour-menu'>
            <Icon icon='question-circled-white' />
            <span className='mr-1'>{text('getStarted')}</span>
          </Dropdown.Toggle>

          <FloatingPortal id='tour-menu-floating-portal'>
            <Dropdown.Menu>
              <div className='position-absolute' style={{ right: 8 }}>
                <IconButton
                  // @ts-expect-error IconButton not typed
                  icon='circled-close'
                  onClick={() => toggleDropdown(false)}
                />
              </div>

              <Dropdown.Header className='d-flex flex-column align-items-center'>
                <h4 className='green-1'>{text('Tours')}</h4>
              </Dropdown.Header>
              <Dropdown.Item
                onClick={() => {
                  dispatch(setTourSelected(TOURS.TOUR_2D_MAP));
                  redirect.push(routes.mp.root);
                }}
              >
                {text('getStarted2D')}
              </Dropdown.Item>

              <Dropdown.Divider />

              <Dropdown.Item onClick={handleOpenFileManagerTour}>
                {text('getStartedFileManager')}
              </Dropdown.Item>

              <Dropdown.Divider />

              <Dropdown.Item
                onClick={() => {
                  if (!setSteps || !setCurrentStep) {
                    console.warn('Tour functions not available');
                    return;
                  }
                  dispatch(setTourSelected(TOURS.TOUR_3D_VIEWER));
                  const tour = GET_STARTED_TOURS[TOURS.TOUR_3D_VIEWER];
                  if (!tour) return;
                  setSteps(tour);
                  setCurrentStep(0);
                  redirect.push(routes.view3DExternal.root, {
                    preserveQueryParams: true,
                    queryParams: { tour: true },
                  });
                }}
              >
                {text('getStarted3D')}
              </Dropdown.Item>

              <Dropdown.Divider />

              <Dropdown.Header className='d-flex flex-column align-items-center'>
                <h4 className='green-1'>{text('help')}</h4>
              </Dropdown.Header>

              <Dropdown.Divider />

              <Dropdown.Item
                onClick={() => {
                  window.open(
                    'https://help.pointerra.io/articles/40420-the-viewer-interface',
                    '_blank'
                  );
                }}
              >
                {text('getStartedPointerraHelp')}
              </Dropdown.Item>
            </Dropdown.Menu>
          </FloatingPortal>
        </Dropdown>
      </div>
    )
  );
};

export default GetStartedButton;
